import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Shield, Rocket, Brain, Code, BookOpen, Users, ChefHat, Briefcase } from 'lucide-react';
import { Section, SectionTitle } from '@/components/Section';
import { Button } from '@/components/ui/button';
import { CareerHero } from '@/components/CareerHero';

const timelinePhases = [
  {
    phase: 'Early Foundations',
    period: '1979–1990',
    icon: Code,
    description: 'Entry into computer science during its frontier era. Built foundational expertise in operations, personal computer integration, networking, and desktop application development across small business and enterprise environments.',
  },
  {
    phase: 'Enterprise Systems Development',
    period: '1990–2005',
    icon: Building2,
    description: 'Advanced through senior technology roles leading complex systems integration, database architecture, and digital transformation initiatives across multiple industries.',
  },
  {
    phase: 'Defense & Aerospace',
    period: '2006–2011',
    icon: Shield,
    description: 'Contributed to mission-critical environments including the USAF Satellite Control Network. Developed expertise in high-reliability systems, risk management, and secure infrastructure.',
  },
  {
    phase: 'Entrepreneurship',
    period: '2010–2024',
    icon: Rocket,
    description: 'Founded and operated multiple ventures spanning food service, publishing, and technology. Built businesses from the ground up with hands-on operational leadership.',
  },
  {
    phase: 'AI, Publishing & Platforms',
    period: 'Current',
    icon: Brain,
    description: 'Focused on integrating artificial intelligence into practical business use. Leading multiple AI-driven ventures while authoring over 40 titles on technology, entrepreneurship, and personal development.',
  },
];

const selectedExperience = [
  {
    title: 'Honeywell / L3 Communications',
    description: 'Senior technology roles supporting the Air Force Satellite Control Network. Led systems development, risk management tooling, and reporting infrastructure for mission-critical defense operations.',
    impact: 'Defense & Aerospace Systems',
  },
  {
    title: 'Momma Pearl\'s Cajun Kitchen',
    description: 'Founded and operated a full-service restaurant bringing authentic Louisiana cuisine to Colorado Springs. Managed every aspect from kitchen operations to business development and team leadership.',
    impact: 'Entrepreneurship & Operations',
  },
  {
    title: 'Current Ventures',
    description: 'Leading AI Means Business (consulting), Use What You Got Publishing, NewAudia (interactive audiobooks), LifeCapsule AI (digital legacy), and Digital Ink AI—each built on practical AI integration.',
    impact: 'AI & Platform Development',
  },
];

const systemsBuilt = [
  'AFSCN satellite control support systems',
  'Enterprise risk management tools',
  'Automated reporting and analytics systems',
  'LifeCapsule AI — digital legacy preservation platform',
  'Digital Ink AI — AI-assisted creative tools',
  'NewAudia — interactive audiobook platform',
  'Use What You Got Publishing — AI-integrated publishing workflow',
];

const technicalFoundation = {
  'Languages & Development': ['JavaScript', 'SQL', 'VBA', 'C#', '.NET', 'HTML/CSS', 'Emergent.sh'],
  'Systems & Infrastructure': ['Windows Server', 'Linux', 'Active Directory', 'Networking', 'Cloud Computing', 'Virtualization'],
  'Data & Analytics': ['Database Architecture', 'SQL Server', 'Oracle', 'Data Migration', 'Reporting Systems', 'Business Intelligence'],
  'AI & Emerging Tech': ['Machine Learning Integration', 'Natural Language Processing', 'AI Workflow Design', 'Prompt Engineering', 'API Integration'],
};

export default function CareerPage() {
  return (
    <main className="pt-20">
      <CareerHero />

      {/* Section A: Career Timeline */}
      <Section dark>
        <SectionTitle subtitle="Timeline">Career Evolution</SectionTitle>
        <div className="relative max-w-4xl mx-auto">
          <div className="hidden md:block absolute left-8 top-0 bottom-0 w-0.5 bg-border/40" />
          <div className="space-y-8">
            {timelinePhases.map((item, index) => (
              <motion.div
                key={item.phase}
                className="relative flex gap-6 md:ml-0"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="hidden md:flex items-start">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0 z-10">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div className="flex-1 bg-card/40 border border-border/20 rounded-xl p-5 hover:border-border/40 transition-colors">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <span className="text-sm text-primary font-semibold uppercase tracking-wider">{item.period}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{item.phase}</h3>
                  <p className="text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Section B: Early Career & Foundations */}
      <Section>
        <SectionTitle subtitle="Foundations">Early Career & Foundations</SectionTitle>
        <div className="max-w-4xl mx-auto space-y-5">
          <motion.p
            className="text-lg text-slate-300 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            The career began not in a classroom, but in the field—working with early computing systems at a time when the industry itself was still being defined. From Halliburton's operational environments to Datec's technology services, the early years were defined by hands-on problem solving, direct client engagement, and learning how systems actually work in practice.
          </motion.p>
          <motion.p
            className="text-lg text-slate-300 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            A pivotal experience came during the Louisiana World Exposition, where technology met large-scale event operations. This was followed by work with Unisys at NAS Pt. Mugu in California—a first experience with DOD contracting that introduced enterprise-grade systems and the discipline of defense environments. A period in sales built a deep understanding of how technology decisions are made from the business side—not just the engineering side.
          </motion.p>
          <motion.p
            className="text-lg text-slate-300 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            These early experiences established a pattern that has held throughout: technology is most valuable when it's integrated into real operations, used by real people, and measured by real outcomes. That belief has guided every role, every venture, and every system built since.
          </motion.p>
        </div>
      </Section>

      {/* Section C: Selected Experience */}
      <Section dark>
        <SectionTitle subtitle="Highlights">Selected Experience</SectionTitle>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {selectedExperience.map((exp, index) => (
            <motion.div
              key={exp.title}
              className="bg-card/40 border border-border/20 rounded-xl p-6 hover:border-primary/30 transition-colors"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              data-testid={`experience-card-${index}`}
            >
              <span className="text-xs text-primary font-semibold uppercase tracking-wider mb-3 block">{exp.impact}</span>
              <h3 className="text-lg font-bold text-white mb-3">{exp.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{exp.description}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Section D: Systems & Platforms Built */}
      <Section>
        <SectionTitle subtitle="Systems">Systems & Platforms Built</SectionTitle>
        <div className="max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-3">
            {systemsBuilt.map((system, index) => (
              <motion.div
                key={system}
                className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30 border border-border/10"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2.5 flex-shrink-0" />
                <span className="text-slate-300">{system}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Section E: Entrepreneurship & Leadership */}
      <Section dark>
        <SectionTitle subtitle="Ventures">Entrepreneurship & Leadership</SectionTitle>
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-4">
              <ChefHat className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-bold text-white">House Chef / Momma Pearl's Cajun Kitchen</h3>
            </div>
            <p className="text-slate-400 leading-relaxed">
              Founded and operated a professional catering service and full-service restaurant in Colorado Springs. Managed all aspects of business operations—kitchen, staffing, customer relations, supply chain, and financial management. This experience brought a hands-on understanding of operational complexity that directly informs technology consulting work today.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <Briefcase className="w-6 h-6 text-primary" />
              <h3 className="text-xl font-bold text-white">Business Operations</h3>
            </div>
            <p className="text-slate-400 leading-relaxed">
              Across every venture—whether technology, publishing, or food service—the common thread has been building systems that work and leading teams that deliver. The entrepreneurial chapters weren't a departure from the technology career; they were an extension of it, applying systems thinking to real-world business problems.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Section F: Technical Foundation */}
      <Section>
        <SectionTitle subtitle="Expertise">Technical Foundation</SectionTitle>
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(technicalFoundation).map(([category, skills], index) => (
            <motion.div
              key={category}
              className="bg-card/40 border border-border/20 rounded-xl p-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              data-testid={`tech-category-${index}`}
            >
              <h3 className="text-sm font-semibold text-primary uppercase tracking-wider mb-4">{category}</h3>
              <ul className="space-y-2">
                {skills.map((skill) => (
                  <li key={skill} className="text-sm text-slate-400 flex items-start gap-2">
                    <div className="w-1 h-1 rounded-full bg-primary/60 mt-2 flex-shrink-0" />
                    {skill}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Section G: Authorship & Thought Leadership */}
      <Section dark>
        <SectionTitle subtitle="Author">Authorship & Thought Leadership</SectionTitle>
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            className="text-lg text-slate-300 leading-relaxed mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Over 40 published titles spanning artificial intelligence, entrepreneurship, and personal development. From the Artificial Intelligence Survival Guide series to Use What You Got and Make a Lot, each book is applied thinking—designed to help people make sense of what's changing and act on it.
          </motion.p>
          <motion.p
            className="text-slate-400 leading-relaxed mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Writing isn't separate from the work—it's part of it. Every title reflects real experience, real systems, and a belief that knowledge should be accessible, practical, and empowering.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
          >
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="view-books-btn">
              <Link to="/books">Explore the Full Library <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </motion.div>
        </div>
      </Section>

      {/* Section H: Expanded Career History CTA */}
      <Section>
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Full Career History
            </h2>
            <p className="text-lg text-slate-400 mb-8">
              For a comprehensive view of the complete professional record—roles, timelines, and detailed experience across every chapter—explore the full career history.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="full-history-btn">
                <Link to="/career-history">View Full Career History <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="schedule-career-btn">
                <Link to="/schedule">Schedule a Conversation</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </Section>
    </main>
  );
}
